import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';

import ROUTES from '@src/constants/route';
import actions from '@src/redux/actions';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import NewPackageInfoV3 from '@src/containers/Layout/SidebarNew/NewPackageInfoV3';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import { Box, IconButton, Typography } from '@mui/material';
import { ArrowBackRounded as ArrowBackRoundedIcon } from '@mui/icons-material';
import {
  MAX_TITLE_LENGTH,
  PROJECT_PRODUCT,
  PROJECT_TYPE,
  TRANSLATION_STATUS,
} from '@src/constants/project';
import useElementWidth from '@src/hooks/useElementWidth';
import useProjectExportPoll from '@src/hooks/useProjectExportPoll';
import { countTextLengthBlocks } from '@src/services/project';
import NewActionsHeader from '@src/containers/Layout/NewActionsHeader';
import { NEW_ACTIONS_HEADER_MENU } from '@src/constants';
import { COLOR } from '@src/styles/color';
import {
  StyledHeader,
  StyledProjectTitle,
  StyledSidebarTooltip,
} from './index.style';
import DownloadButton from './DownloadButton';
import DubbingDownloadButton from './DubbingDownloadButton';
import VideoDubbingDownloadButton from './VideoDubbingDownloadButton';
import PresentationDownloadButton from './PresentationDownloadButton';
import SaveButton from './SaveButton';
import AutoSaveStatus from './AutoSaveStatus';
import UndoRedoControls from './UndoRedoControls';
import PresentationFeedbackButton from './PresentationFeedbackButton';

const WIDTH_DIVIDER = 400;

const Header = () => {
  const titleRef = useRef();
  const titleWidth = useElementWidth(titleRef);
  const history = useHistory();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { language } = i18n;
  const dispatch = useDispatch();
  const { getFeatureValue } = useFeatureFlags();

  const { title, multiBlock, product, translation, source } = useSelector(
    (state) => state.project,
  );
  const blocks = multiBlock?.blocks;

  // Mounted here because this is the one ancestor every download button shares,
  // and exactly one of them renders for a project.
  useProjectExportPoll({ projectId: location.pathname.split('/')[2] });

  const hasTranslation =
    product === PROJECT_PRODUCT.DUBBING &&
    translation?.status === TRANSLATION_STATUS.SUCCESS;
  const textField = hasTranslation ? 'translatedText' : 'text';

  const user = useSelector((state) => state.auth.user);
  const { openSidebar } = useSelector((state) => state.layout);
  const autoSaveEnabled = useSelector((s) => s.project.autoSaveEnabled);
  const [newTitle, setNewTitle] = useState(title);
  const useNewSidebarV2 = getFeatureValue(FEATURE_KEYS.NEW_SIDEBAR_V2, {
    userId: user.id,
    email: user.email,
    phoneNumber: user.phoneNumber,
  });

  const isBlockUIImprovement = getFeatureValue(
    FEATURE_KEYS.PROJECT_TTS_IMPROVE_BLOCK_UI,
    { userId: user?.id, email: user?.email },
  );

  const isVideoSource = source?.type === PROJECT_TYPE.VIDEO;

  const videoDubbingDownloadAudioEnabled = getFeatureValue(
    FEATURE_KEYS.VIDEO_DUBBING_DOWNLOAD_AUDIO,
    { userId: user?.id, email: user?.email },
  );

  const useVideoDubbingDownload =
    product === PROJECT_PRODUCT.DUBBING &&
    isVideoSource &&
    videoDubbingDownloadAudioEnabled;

  const useDubbingDownload =
    product === PROJECT_PRODUCT.DUBBING &&
    isBlockUIImprovement &&
    !useVideoDubbingDownload;

  const hasPresentationExport = getFeatureValue(
    FEATURE_KEYS.PRESENTATION_EXPORT,
    { userId: user?.id, email: user?.email },
  );

  const usePresentationDownload =
    product === PROJECT_PRODUCT.PRESENTATION && hasPresentationExport;

  const showUndoRedo =
    product === PROJECT_PRODUCT.DUBBING &&
    getFeatureValue(FEATURE_KEYS.PROJECT_DUBBING_UNDO_REDO, {
      userId: user?.id,
      email: user?.email,
    });

  const showPresentationFeedback =
    product === PROJECT_PRODUCT.PRESENTATION &&
    getFeatureValue(FEATURE_KEYS.PRESENTATION_PROJECT_FEEDBACK, {
      userId: user?.id,
      email: user?.email,
    });

  const handleValidateProjectTitle = (value) => {
    if (value.length <= MAX_TITLE_LENGTH) return true;
    dispatch(
      actions.noti.push({
        severity: 'warning',
        message: 'updateTitleMaxLength',
        value: MAX_TITLE_LENGTH,
      }),
    );
    return false;
  };

  const handleChangeProjectTitle = (e) => {
    const validTitle = handleValidateProjectTitle(e.target.value);
    if (!validTitle) return;
    setNewTitle(e.target.value);
  };

  const updateProjectTitle = async () => {
    if (!newTitle) {
      setNewTitle(title);
      return;
    }
    if (newTitle !== '' && newTitle !== title) {
      dispatch(actions.project.updateTitle(newTitle));
    }
  };

  const userPackageInfo = () => (
    <>
      <div className="container-user-info">
        <div className="container-btn">
          {showPresentationFeedback && <PresentationFeedbackButton />}
          {showUndoRedo && <UndoRedoControls />}
          {!autoSaveEnabled && <SaveButton />}
          {useVideoDubbingDownload && <VideoDubbingDownloadButton />}
          {useDubbingDownload && <DubbingDownloadButton />}
          {usePresentationDownload && <PresentationDownloadButton />}
          {!useVideoDubbingDownload &&
            !useDubbingDownload &&
            !usePresentationDownload && <DownloadButton />}
        </div>

        <div className="divider" />
        <NewActionsHeader
          menuShow={[
            NEW_ACTIONS_HEADER_MENU.TASK_CREDIT,
            NEW_ACTIONS_HEADER_MENU.GUIDE_CREDIT,
            NEW_ACTIONS_HEADER_MENU.NOTIFICATION,
            NEW_ACTIONS_HEADER_MENU.LEADERBOARD_VOICE,
          ]}
          onTaskCredits={() =>
            dispatch(actions.layout.setShowOnboardingPopup(true))
          }
          onLeaderboardVoice={() => {
            history.push(ROUTES.VOICE_LEADERBOARD);
          }}
          margin="0px 16px"
        />
        <div className="wallet">
          <Box className="wallet-character">
            <NewPackageInfoV3 useNewSidebarV2={useNewSidebarV2} />
          </Box>
        </div>
      </div>
    </>
  );

  const renderProjectTitle = () => (
    <StyledProjectTitle
      maxWidth={titleWidth - WIDTH_DIVIDER}
      openSidebar={openSidebar}
    >
      <div className="input-wrapper">
        <span className="size-span">{newTitle}</span>
        <input
          value={newTitle}
          placeholder={t('noTitle')}
          className="size-input"
          onChange={handleChangeProjectTitle}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.target.blur();
            }
          }}
          onBlur={updateProjectTitle}
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Typography className="createdAt">
          {countTextLengthBlocks(blocks, textField).toLocaleString(language)}{' '}
          {t('characters').toLowerCase()}
        </Typography>
        {autoSaveEnabled && (
          <>
            <span style={{ margin: '0 7px', color: COLOR.label2[64] }}>•</span>
            <AutoSaveStatus />
          </>
        )}
      </div>
    </StyledProjectTitle>
  );

  const renderGoBack = () => (
    <Box className="go-back-container">
      <StyledSidebarTooltip title={t('goBack')}>
        <IconButton
          onClick={() => {
            history.push(ROUTES.PROJECTS);
          }}
        >
          <ArrowBackRoundedIcon className="go-back-icon" />
        </IconButton>
      </StyledSidebarTooltip>
    </Box>
  );
  return (
    <StyledHeader ref={titleRef}>
      <div className="container-header">
        {renderGoBack()}
        {renderProjectTitle()}
      </div>
      {userPackageInfo()}
    </StyledHeader>
  );
};

export default Header;
